/**
 * Rendering Engine — editor mapping.
 *
 * One resolved Design JSON V2 node → one native iVisora editor element. Every
 * primitive in the language is mapped; masks become editor frames; groups and
 * frames become group elements with `parentId` members, the editor's own
 * convention.
 *
 * Property names here are the editor's vocabulary. Anything the editor has no
 * native slot for (roles, sections, constraints, editability, relationships,
 * animation hooks) is preserved under `meta` — the editor ignores unknown
 * fields, so nothing is lost and nothing breaks.
 */

import {
  SHAPE_MAP, FRAME_SHAPE_MAP, ELEMENT_TYPES, ICON_NAMES, ICON_SHAPES, ICON_FALLBACK_SHAPE, META_NAMESPACE,
} from './rendererSchema';
import { resolveFills, resolveStroke, resolveEffects } from './paint';
import { applyTextCase } from './tokens';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** Everything the editor has no native home for, kept beside the element. */
const metaOf = (node, ctx, extra = {}) => ({
  [META_NAMESPACE]: {
    role: node.role ?? null,
    layer: node.layer || 'content',
    sectionId: ctx.sectionId ?? null,
    sectionRole: ctx.sectionRole ?? null,
    constraints: isObj(node.constraints) ? { ...node.constraints } : null,
    editable: isObj(node.editable) ? { ...node.editable } : null,
    instanceOf: node.instanceOf ?? null,
    variant: node.variant ?? null,
    // Future animation hooks travel with the element; nothing today reads them.
    motion: isObj(node.motion) ? { ...node.motion } : null,
    relationships: isObj(node.relationships) ? { ...node.relationships } : null,
    renderer: 'designV2/renderer',
    ...extra,
  },
});

/** The properties every element carries. */
const common = (node, ctx, extraMeta) => {
  const editable = isObj(node.editable) ? node.editable : {};
  return {
    id: node.id,
    x: node.x, y: node.y, width: node.width, height: node.height,
    rotation: isNum(node.rotation) ? node.rotation : 0,
    opacity: 1,
    // `locked` is the editor's own flag; the rest of editability lives in meta.
    locked: editable.locked === true,
    visible: true,
    parentId: null,
    ...metaOf(node, ctx, extraMeta),
  };
};

/** Fills, stroke and effects, applied in the order the editor expects. */
const paint = (element, node, ctx) => {
  const { props: fillProps, extra } = resolveFills(node.fills, ctx.tokens);
  const strokeProps = resolveStroke(node.stroke, ctx.tokens);
  const { props: effectProps, unsupported } = resolveEffects(node.effects, ctx.tokens);
  const merged = { ...element, ...fillProps, ...strokeProps, ...effectProps };

  if (extra.length || unsupported.length) {
    merged[META_NAMESPACE] = {
      ...merged[META_NAMESPACE],
      ...(extra.length ? { additionalFills: extra } : {}),
      ...(unsupported.length ? { unsupportedEffects: unsupported } : {}),
    };
  }
  return merged;
};

const MAPPERS = {
  text: (node, ctx) => {
    const style = ctx.tokens.typeStyle(node.style || 'body');
    return paint({
      ...common(node, ctx, { typeStyle: node.style || 'body', textCase: style.case }),
      type: ELEMENT_TYPES.text,
      text: applyTextCase(node.text, style.case),
      fontSize: style.fontSize,
      fontFamily: style.font,          // the editor's font mapping resolves the family
      fontWeight: style.weight,
      color: ctx.tokens.color(node.color, '#111111'),
      textAlign: node.align || 'left',
      lineHeight: style.leading,
      letterSpacing: style.tracking,
    }, node, ctx);
  },

  shape: (node, ctx) => paint({
    ...common(node, ctx),
    type: ELEMENT_TYPES.shape,
    shape: SHAPE_MAP[node.shape] || 'rect',
    fill: 'transparent',
    stroke: 'none',
    strokeWidth: 0,
    cornerRadius: ctx.tokens.radius(node.cornerRadius, 0),
  }, node, ctx),

  image: (node, ctx) => {
    const mask = isObj(node.mask) ? node.mask : null;
    const src = node.src || null;
    // The designer's asset intent must survive into the element, because that
    // is what the image resolver reads. Carrying only `imageQuery` meant a
    // document that used the richer `assetIntent` arrived with no intent at
    // all: nothing was searched for, and the hero stayed a placeholder.
    const intent = { imageQuery: node.query || null, assetIntent: isObj(node.assetIntent) ? node.assetIntent : null };
    // A masked image is an editor frame: the frame shape does the cropping.
    if (mask && (mask.kind === 'shape' || mask.kind === 'clip' || mask.kind === 'image')) {
      return paint({
        ...common(node, ctx, { mask: { ...mask }, ...intent }),
        type: ELEMENT_TYPES.frame,
        shape: FRAME_SHAPE_MAP[mask.shape] || 'rectangle',
        fit: node.fit || 'cover',
        image: src,
      }, node, ctx);
    }
    // No asset yet: the editor's native image drop-zone, carrying the intent.
    if (!src) {
      return paint({
        ...common(node, ctx, { ...intent }),
        type: ELEMENT_TYPES.frame,
        shape: 'rectangle',
        fit: node.fit || 'cover',
        image: null,
      }, node, ctx);
    }
    return paint({
      ...common(node, ctx, { ...intent }),
      type: ELEMENT_TYPES.image,
      src,
      objectFit: node.fit || 'cover',
    }, node, ctx);
  },

  // An icon renders as a real editor SHAPE, never an emoji. Three names have an
  // exact geometric equivalent; every other name — known or not — becomes a
  // deliberate accent disc. The requested name is always preserved in meta, so
  // an editor that later gains icon assets loses nothing.
  icon: (node, ctx) => {
    const name = node.icon ?? null;
    const known = ICON_NAMES.includes(name);
    return paint({
      ...common(node, ctx, { iconName: name, iconKnown: known }),
      type: ELEMENT_TYPES.shape,
      shape: ICON_SHAPES[name] || ICON_FALLBACK_SHAPE,
      cornerRadius: 0,
    }, node, ctx);
  },

  // The editor has no vector path primitive: render the node's box and keep the
  // path in meta so a future path-capable editor loses nothing.
  vector: (node, ctx) => paint({
    ...common(node, ctx, { vectorPath: node.path ?? null }),
    type: ELEMENT_TYPES.shape,
    shape: SHAPE_MAP[node.shape] || 'rect',
    fill: 'transparent',
    stroke: 'none',
    strokeWidth: 0,
    cornerRadius: 0,
  }, node, ctx),

  // A line is a rule: a thin rect is the editor's native equivalent.
  line: (node, ctx) => paint({
    ...common(node, ctx, { primitive: 'line' }),
    type: ELEMENT_TYPES.shape,
    shape: 'rect',
    fill: ctx.tokens.color(isObj(node.stroke) ? node.stroke.fill : null, '#111111'),
    stroke: 'none',
    strokeWidth: 0,
    cornerRadius: 0,
  }, node, ctx),
};

/**
 * True when a frame clips a single image.
 *
 * A frame is a shaped clipping container, and the editor has exactly that
 * primitive — so the frame and its image collapse into one native frame
 * element rather than a group wrapping a picture.
 */
export const framesImage = (node) => isObj(node)
  && node.node === 'frame'
  && isObj(node.child)
  && node.child.type === 'image'
  && !Array.isArray(node.children);

/** A group or frame → the editor's group element (or native frame, when it clips an image). */
export const mapContainer = (node, ctx) => {
  if (framesImage(node)) {
    const child = node.child;
    return paint({
      ...common(node, ctx, { container: 'frame', clip: node.clip ?? null, imageQuery: child.query ?? null, childId: child.id ?? null }),
      type: ELEMENT_TYPES.frame,
      shape: FRAME_SHAPE_MAP[node.clip] || 'rectangle',
      fit: child.fit || 'cover',
      image: child.src || null,
      cornerRadius: ctx.tokens.radius(node.cornerRadius, 0),
    }, node, ctx);
  }

  return paint({
    ...common(node, ctx, { container: node.node, layout: isObj(node.layout) ? { ...node.layout } : null }),
    type: ELEMENT_TYPES.group,
    name: node.role || node.id || 'Group',
    derivedBounds: { x: node.x, y: node.y, width: node.width, height: node.height },
  }, node, ctx);
};

/**
 * Map one resolved node to a native editor element.
 * @returns {(Object|null)} null when the node has no editor representation.
 */
export const mapNode = (node, ctx) => {
  if (!isObj(node)) return null;
  if (node.node === 'group' || node.node === 'frame') return mapContainer(node, ctx);
  const mapper = MAPPERS[node.type];
  // An unknown primitive renders as its box rather than disappearing silently.
  return mapper ? mapper(node, ctx) : MAPPERS.shape({ ...node, shape: node.shape || 'rect' }, ctx);
};

export const __internals = { metaOf, paint };
